import { Select as BaseSelect } from '@base-ui/react/select'
import React from 'react'
import styled from 'styled-components'
import { optionText } from 'sutra/components/Select/select-options'
import {
  EmptyNote,
  listStyles,
  optionStyles,
  type PopupWidth,
  selectPopupStyles,
  positionerStyles,
  type SelectSize,
  triggerStyles,
} from 'sutra/components/Select/select-styles'
import type {
  SelectOption,
  SelectValue,
} from 'sutra/components/Select/select-types'
import { SelectTriggerContent } from 'sutra/components/Select/SelectAdornments'
import { OptionContent } from 'sutra/components/Select/SelectOptionRow'
import {
  type SingleSelectProps,
  useSelectSingle,
} from 'sutra/components/Select/use-select-single'

// The look is shared with MultiSelect (see select-styles); only the parts bound
// to Base UI's Select primitive are declared here.

const SelectTrigger = styled(BaseSelect.Trigger)<{ $size: SelectSize }>`
  ${triggerStyles}
`

const SelectPositioner = styled(BaseSelect.Positioner)<{ $zIndex: number }>`
  ${positionerStyles}
`

const SelectPopup = styled(BaseSelect.Popup).attrs<{
  'data-sutra-select-panel'?: string
}>({
  'data-sutra-select-panel': '',
})<PopupWidth>`
  ${selectPopupStyles}
`

const SelectList = styled(BaseSelect.List)`
  ${listStyles}
`

const SelectItem = styled(BaseSelect.Item)`
  ${optionStyles}
`

// A plain single-value dropdown on Base UI's Select; the panel portals to
// `document.body`, so it escapes any `overflow: auto` ancestor and any
// caller-local z-index.
export function SelectSingle<T extends SelectValue, O extends SelectOption<T>>(
  props: SingleSelectProps<T, O>,
): React.ReactElement {
  const state = useSelectSingle(props)
  const { size = 'sm', zIndex = 1000, panelAlign = 'start' } = props

  return (
    <BaseSelect.Root
      value={state.current ?? null}
      onValueChange={(next) => {
        if (next !== null) state.commit(next)
      }}
      open={state.isOpen}
      onOpenChange={state.handleOpenChange}
      disabled={props.disabled}
    >
      <SelectTrigger $size={size} {...state.triggerProps}>
        <SelectTriggerContent
          labelId={state.labelId}
          label={state.triggerLabel}
          showPlaceholder={state.showPlaceholder}
          icon={props.triggerIcon}
          accessory={props.triggerAccessory}
          onClear={state.showClear ? state.clear : undefined}
          loading={props.loading}
          suffixIcon={props.suffixIcon}
        />
      </SelectTrigger>
      <BaseSelect.Portal>
        <SelectPositioner
          side="bottom"
          align={panelAlign}
          sideOffset={4}
          alignItemWithTrigger={false}
          $zIndex={zIndex}
        >
          <SelectPopup
            className={props.popupClassName}
            $popupWidth={props.popupMatchSelectWidth}
          >
            <SelectList>
              {state.all.map((option) => (
                <SelectItem
                  key={String(option.value)}
                  value={option.value}
                  label={optionText(option)}
                  disabled={option.disabled}
                >
                  <OptionContent
                    option={option}
                    optionRender={props.optionRender}
                  />
                </SelectItem>
              ))}
            </SelectList>
            {state.all.length === 0 && <EmptyNote>{state.emptyText}</EmptyNote>}
          </SelectPopup>
        </SelectPositioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  )
}
